/**
 * 接口请求加解密工具
 *
 * 对应 52dsy-vue2 项目 project/api/http/encrypt.js
 *
 * 加密流程（请求时）：
 *  1. 生成 16 字节随机 AES 密钥
 *  2. 用 RSA 公钥加密 AES 密钥（jsencrypt）
 *  3. 用 AES-CBC + Pkcs7 加密参数（JSON.stringify 后）
 *  4. 生成 HMAC-SHA256 签名（基于 encryptedParams + timestamp）
 *  5. 返回 { encryptedAesKeyFromFrontend, encryptedParamsFromFrontend, signatureFromFrontend, timestampFromFrontend }
 *
 * 解密流程（响应时）：
 *  - data.data.encryptedData 字段使用约定的固定 aesSecret 进行 AES-CBC 解密
 */
// 按需引入 crypto-js 子模块
import CryptoJS from 'crypto-js/core';
import { JSEncrypt } from 'jsencrypt';
import { HMAC_SECRET, RSA_PUBLIC_KEY } from './encrypt-config';
import 'crypto-js/aes';
import 'crypto-js/cipher-core';
import 'crypto-js/enc-base64';
import 'crypto-js/enc-hex';
import 'crypto-js/enc-utf8';
import 'crypto-js/hmac-sha256';
import 'crypto-js/mode-ecb';
import 'crypto-js/pad-pkcs7';
import 'crypto-js/sha256';

/**
 * 生成随机 WordArray（兼容 APP 环境）
 *
 * 原因：Vue2 项目使用 webpack 构建，webpack 会自动为 Node.js crypto 模块注入 polyfill，
 * 所以 CryptoJS.lib.WordArray.random() 在 APP 环境中可正常工作。
 * 而 Vue3 项目使用 Vite 构建，Vite 不会 polyfill Node.js 内置模块，
 * 导致 crypto-js 内部调用 require("crypto") 时报错：
 *   "Native crypto module could not be used to get secure random number"
 *
 * 此方法直接使用 Math.random() 生成伪随机字节，构造 WordArray，
 * 彻底绕开 crypto-js 内部的 SecureRandom 机制，确保在所有环境下均可运行。
 */
function generateRandomWordArray(byteLength: number): CryptoJS.lib.WordArray {
  const words: number[] = [];
  for (let i = 0; i < byteLength; i += 4) {
    let word = 0;
    for (let j = 0; j < 4 && (i + j) < byteLength; j++) {
      word = (word << 8) | Math.floor(Math.random() * 256);
    }
    words.push(word);
  }
  return CryptoJS.lib.WordArray.create(words, byteLength);
}

interface EncryptOptions {
  /** RSA 公钥（base64 单行） */
  rsaPublicKey: string
  /** RSA 私钥（base64 单行，可选） */
  rsaPrivateKey?: string
  /** HMAC 密钥 */
  hmacSecret: string
}

/** 加密后用于发送给服务端的数据结构 */
export interface FrontendEncryptedPayload {
  encryptedAesKeyFromFrontend: string
  encryptedParamsFromFrontend: string
  signatureFromFrontend: string
  timestampFromFrontend: number
}

export class ApiEncrypt {
  private rsaPublicKeyPem: string = '';
  private rsaPrivateKeyPem: string = '';
  private hmacSecret: string;

  constructor(config: EncryptOptions) {
    this.hmacSecret = config.hmacSecret;
    if (config.rsaPublicKey) {
      this.rsaPublicKeyPem = this.base64ToPem(config.rsaPublicKey, 'PUBLIC KEY');
    }
    if (config.rsaPrivateKey) {
      this.rsaPrivateKeyPem = this.base64ToPem(config.rsaPrivateKey, 'PRIVATE KEY');
    }
  }

  // ====== RSA ======
  rsaEncrypt(aesKey: CryptoJS.lib.WordArray): string {
    if (!this.rsaPublicKeyPem) {
      throw new Error('请先初始化 RSA 公钥');
    }
    const aesKeyHex = CryptoJS.enc.Hex.stringify(aesKey);
    const encryptor = new JSEncrypt();
    encryptor.setPublicKey(this.rsaPublicKeyPem);
    const result = encryptor.encrypt(aesKeyHex);
    if (result === false) {
      throw new Error('RSA 加密失败');
    }
    return result as string;
  }

  // ====== AES-CBC 加密 ======
  aesEncrypt(data: string, aesKey: CryptoJS.lib.WordArray): string {
    const iv = generateRandomWordArray(16);
    const encrypted = CryptoJS.AES.encrypt(data, aesKey, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    const encryptedBase64 = encrypted.toString();
    const ivBase64 = CryptoJS.enc.Base64.stringify(iv);
    return `${ivBase64}:${encryptedBase64}`;
  }

  // ====== AES-CBC 解密（响应数据） ======
  aesDecrypt(encryptedData: string, frontendAesSecret: string): string {
    const parts = encryptedData.split(':');
    if (parts.length < 2) {
      throw new Error('加密数据格式错误');
    }
    // parts[0] = iv (base64), parts[1] = ciphertext (base64)
    const secretKey = CryptoJS.enc.Base64.parse(frontendAesSecret);
    const iv = CryptoJS.enc.Base64.parse(parts[0]);
    // 部分场景密文中可能包含 ":" 字符，使用 splice 拼回
    const ciphertext = parts.slice(1).join(':');
    const decrypted = CryptoJS.AES.decrypt(ciphertext, secretKey, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  }

  // ====== HMAC-SHA256 签名 ======
  generateHmac(data: string, timestamp: number): string {
    const content = data + timestamp;
    const hmac = CryptoJS.HmacSHA256(
      CryptoJS.enc.Utf8.parse(content),
      CryptoJS.enc.Utf8.parse(this.hmacSecret),
    );
    return CryptoJS.enc.Base64.stringify(hmac);
  }

  /**
   * 生成发送给服务端的加密参数（请求体）
   */
  generateFrontendData(postData: Record<string, any>): FrontendEncryptedPayload {
    const aesKey = generateRandomWordArray(16);
    const encryptedAesKey = this.rsaEncrypt(aesKey);
    const encryptedParams = this.aesEncrypt(JSON.stringify(postData), aesKey);
    const timestamp = Date.now();
    const signature = this.generateHmac(encryptedParams, timestamp);
    return {
      encryptedAesKeyFromFrontend: `${encryptedAesKey}:M`,
      encryptedParamsFromFrontend: encryptedParams,
      signatureFromFrontend: signature,
      timestampFromFrontend: timestamp,
    };
  }

  // ====== 工具：base64 -> PEM ======
  private base64ToPem(base64: string, label: string): string {
    const base64Data = base64.replace(/\s/g, '');
    return `-----BEGIN ${label}-----\n${this.chunkString(base64Data, 64)}\n-----END ${label}-----\n`;
  }

  private chunkString(str: string, chunkSize: number): string {
    const chunks: string[] = [];
    for (let i = 0; i < str.length; i += chunkSize) {
      chunks.push(str.substring(i, i + chunkSize));
    }
    return chunks.join('\n');
  }
}

// ====== 全局密钥常量（与 vue2 项目保持一致） ======
// 已迁移至 encrypt-config.ts

let defaultInstance: ApiEncrypt | null = null;
export function getApiEncrypt(): ApiEncrypt {
  if (!defaultInstance) {
    defaultInstance = new ApiEncrypt({
      rsaPublicKey: RSA_PUBLIC_KEY,
      hmacSecret: HMAC_SECRET,
    });
  }
  return defaultInstance;
}
